/**
 * Pure helpers for the Admin page: byte/size formatting, compact ingest-count
 * rendering, and calendar-month arithmetic for the AI-usage month picker. Kept
 * dependency-free and unit-tested.
 */

/** Ingest insert counts as stored in `ingest_events.inserted`. */
export interface InsertedCounts {
  metrics: number;
  sleep: number;
  workouts: number;
}

/** Bytes → a compact human size: "820 B", "1.4 KB", "3.2 MB". */
export function fmtBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${trim(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${trim(mb)} MB`;
  return `${trim(mb / 1024)} GB`;
}

/** One decimal, trailing zero dropped: 1.0 → "1", 1.45 → "1.5". */
function trim(n: number): string {
  return String(Number(n.toFixed(1)));
}

/**
 * Compact ingest counts: `{ metrics: 38, sleep: 1, workouts: 0 }` → "38 metrics
 * · 1 sleep". Zero-count kinds are dropped; an all-zero payload renders "0".
 */
export function fmtInsertedCounts(inserted: InsertedCounts | null | undefined): string {
  if (!inserted) return '—';
  const parts: string[] = [];
  if (inserted.metrics) parts.push(`${inserted.metrics} metrics`);
  if (inserted.sleep) parts.push(`${inserted.sleep} sleep`);
  if (inserted.workouts) parts.push(`${inserted.workouts} ${inserted.workouts === 1 ? 'workout' : 'workouts'}`);
  return parts.length > 0 ? parts.join(' · ') : '0';
}

/** Milliseconds → "0 ms", "940 ms", "1.4 s". */
export function fmtDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${trim(ms / 1000)} s`;
}

// --- Month picker ----------------------------------------------------------

/** The current calendar month as `YYYY-MM`, in the viewer's local zone. */
export function currentMonth(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Shift a `YYYY-MM` string by `delta` months (negative = earlier). Rolls years
 * correctly: `shiftMonth('2026-01', -1)` → '2025-12'.
 */
export function shiftMonth(month: string, delta: number): string {
  const m = month.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month;
  // Convert to a 0-based absolute month index, shift, convert back.
  const idx = Number(m[1]) * 12 + (Number(m[2]) - 1) + delta;
  const year = Math.floor(idx / 12);
  const mo = (idx % 12) + 1;
  return `${String(year).padStart(4, '0')}-${String(mo).padStart(2, '0')}`;
}

/** `YYYY-MM` → "March 2026" for display. Invalid input passes through. */
export function monthLabel(month: string): string {
  const m = month.match(/^(\d{4})-(\d{2})$/);
  if (!m) return month;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** True when `month` is the current month or later (disables the "next" arrow). */
export function isAtCurrentMonth(month: string, now: Date = new Date()): boolean {
  return month >= currentMonth(now);
}

/**
 * True when `iso` is within `maxMinutes` of `now` — drives the "live" pulse dot
 * on a fresh ingest event. Defaults to a 90-minute window.
 */
export function isRecent(iso: string, maxMinutes = 90, now: Date = new Date()): boolean {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return false;
  const ageMs = now.getTime() - t;
  return ageMs >= 0 && ageMs <= maxMinutes * 60_000;
}
